/**
 * Sub / vendor row actions — single overflow menu (portal link + statement).
 */
import { useState, type CSSProperties } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import { resendPortalLink } from '../../api/subs'
import {
  createStatementMagicLink,
  exportStatementPdf,
  emailStatement,
  type StatementPreviewParams,
} from '../../api/statements'
import { isProdEnvironment } from '../../lib/appEnv'
import { useActionToast } from '../../lib/useActionToast'
import { actionErrorMessage } from '../../lib/actionErrorMessage'
import { track } from '../../lib/track'

export interface SubVendorActionsMenuProps {
  /** Address-book vendors.id — null when unresolved (do not pass vendor user id). */
  vendorId: number | null
  vendorUserId: number | null
  companyName: string
  ownerEmail?: string
  testId?: string
}

const menuContentStyle: CSSProperties = {
  minWidth: 220,
  background: '#fff',
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: '6px 0',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  zIndex: 400,
}

const menuItemStyle: CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  color: 'var(--ink)',
  cursor: 'pointer',
  outline: 'none',
}

export default function SubVendorActionsMenu({
  vendorId,
  vendorUserId,
  companyName,
  ownerEmail,
  testId = 'sub-actions-menu',
}: SubVendorActionsMenuProps) {
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const { showToast, Toast } = useActionToast()

  const statementParams: Omit<StatementPreviewParams, 'direction'> = {
    sub_vendor_user_id: vendorUserId ?? undefined,
    sub_label: companyName,
  }

  const statementBody = { direction: 'sub' as const, ...statementParams }

  async function runPortalAction(copyOnly: boolean) {
    if (vendorId == null) {
      showToast('Vendor not in address book — cannot send portal link')
      return
    }
    setBusy(true)
    try {
      const data = await resendPortalLink(vendorId)
      if (copyOnly) {
        await navigator.clipboard.writeText(data.invite_url)
        showToast('Portal link copied')
      } else {
        showToast('Portal link sent')
      }
    } catch (e) {
      showToast(actionErrorMessage(e, 'Portal link failed'))
    } finally {
      setBusy(false)
    }
  }

  async function onViewStatement() {
    if (!vendorUserId) {
      showToast('No linked vendor user — cannot open statement')
      return
    }
    setBusy(true)
    try {
      const blob = await exportStatementPdf(statementBody)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'sub-statement.pdf'
      a.click()
      URL.revokeObjectURL(url)
      showToast('Statement downloaded')
    } catch (e) {
      showToast(actionErrorMessage(e, 'Export failed'))
    } finally {
      setBusy(false)
    }
  }

  async function onCopyStatementLink() {
    if (!vendorUserId) {
      showToast('No linked vendor user — cannot copy statement link')
      return
    }
    setBusy(true)
    try {
      const data = await createStatementMagicLink(statementBody)
      await navigator.clipboard.writeText(data.url)
      showToast('Statement link copied')
    } catch (e) {
      showToast(actionErrorMessage(e, 'Link failed'))
    } finally {
      setBusy(false)
    }
  }

  async function onEmailStatement() {
    if (!vendorUserId) {
      showToast('No linked vendor user — cannot email statement')
      return
    }
    const to = window.prompt('Recipient email', ownerEmail ?? '')?.trim()
    if (!to) return
    setBusy(true)
    try {
      const data = await emailStatement({ ...statementBody, to })
      if (data.sent === false && data.reason === 'blocked_by_allowlist') {
        const blockedMsg = !isProdEnvironment()
          ? `Not sent — ${data.recipient ?? to} isn't on the staging allowlist`
          : `Not sent — ${data.recipient ?? to} could not receive email`
        showToast(blockedMsg)
        return
      }
      showToast('Email sent')
      track('statement_emailed', {
        project_id: statementBody.project_id ?? undefined,
        project_count: statementBody.project_ids?.length ?? (statementBody.project_id ? 1 : 0),
      })
    } catch (e) {
      showToast(actionErrorMessage(e, 'Email failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="btn b-gh btn-sm"
            disabled={busy}
            aria-label="Sub actions"
            data-testid={testId}
            onClick={e => e.stopPropagation()}
            style={{ padding: '4px 8px', minWidth: 32 }}
          >
            <MoreHorizontal size={16} strokeWidth={2} color="var(--ink)" aria-hidden />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="sub-actions-menu-content"
            sideOffset={6}
            align="end"
            style={menuContentStyle}
            onClick={e => e.stopPropagation()}
          >
            <DropdownMenu.Item
              className="sub-actions-menu-item"
              data-testid={`${testId}-resend-portal`}
              disabled={busy || vendorId == null}
              onSelect={() => { void runPortalAction(false) }}
              style={menuItemStyle}
            >
              Resend portal link
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="sub-actions-menu-item"
              data-testid={`${testId}-copy-portal`}
              disabled={busy || vendorId == null}
              onSelect={() => { void runPortalAction(true) }}
              style={menuItemStyle}
            >
              Copy portal link
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="sub-actions-menu-item"
              data-testid={`${testId}-view-statement`}
              disabled={busy || !vendorUserId}
              onSelect={() => { void onViewStatement() }}
              style={menuItemStyle}
            >
              View statement
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="sub-actions-menu-item"
              data-testid={`${testId}-copy-statement`}
              disabled={busy || !vendorUserId}
              onSelect={() => { void onCopyStatementLink() }}
              style={menuItemStyle}
            >
              Copy statement link
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="sub-actions-menu-item"
              data-testid={`${testId}-email-statement`}
              disabled={busy || !vendorUserId}
              onSelect={() => { void onEmailStatement() }}
              style={menuItemStyle}
            >
              Email statement
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {Toast}
    </>
  )
}
